import { useState } from "react";
import { SessionNoteRow, SessionNoteRowProps } from "../../session-notes/SessionNoteRow";
import { AutomatedProctoringSessionNote } from "../hooks/useSessionNotes";
import { Thumbnail } from "../hooks/useThumbnails";
import i18n from "../../../../../i18n";
import { CoreSpinner, CoreTextBox, coreToast } from "@verity/components/core";
import PaperPlaneIcon from "../../../../images/paper-plane-right.svg?react";
import NotesIcon from "../../../../../images/reports/notesIcon.svg?react";
import { NoResults } from "../../../../../reports/shared/report-components";
import styles from "./NotePanel.module.css";
import { Typography } from "@mui/material";

type RoomScanNotePanelProps = {
	className?: string;
} & Omit<NotesPanelProps, "className">;

export function RoomScanNotePanel({ className, ...props }: RoomScanNotePanelProps) {
	return (
		<div className={`${styles.roomScanNotesContainer} ${className ?? ""}`}>
			<div className={styles.roomScanNotesHeader}>
				<div className={styles.roomScanNotesIcon}>
					<NotesIcon />
				</div>
				<Typography className={styles.roomScanNotesTitle}>
					{i18n.t("result.components.resultsPage.proctoringResultsViewTSX.roomScanNotes")}
				</Typography>
			</div>
			<div className={styles.roomScanNotesWrapper}>
				<NotesPanel {...props} className={styles.roomScanNotesPanel} notesInputClassName={styles.roomScanNotesInput} />
			</div>
		</div>
	);
}

type NotesPanelProps = {
	sessionNotes: AutomatedProctoringSessionNote[];
	thumbnails: Thumbnail[];
	onAddNote: (content: string) => Promise<boolean | undefined>;
	className?: string;
	notesInputClassName?: string;
} & Pick<
	SessionNoteRowProps<AutomatedProctoringSessionNote>,
	"editNote" | "deleteNote" | "dispatch" | "duration" | "offsetGetter"
>;

export default function NotesPanel({
	sessionNotes,
	thumbnails,
	editNote,
	deleteNote,
	dispatch,
	duration,
	onAddNote,
	offsetGetter: timeOffset,
	className,
	notesInputClassName,
}: NotesPanelProps) {
	const [sideBarNotesNewInput, setSideBarNotesNewInput] = useState("");
	const [waitingAddNote, setWaitingAddNote] = useState(false);

	const handleAddNote = async () => {
		setWaitingAddNote(true);
		try {
			const success = await onAddNote(sideBarNotesNewInput);
			if (success) {
				setSideBarNotesNewInput("");
				return;
			}
			throw new Error("Failed to add note");
		} catch {
			coreToast.error(i18n.t("result.components.studentSelection.videoPlayer.videoPlayerTSX.addNoteFail"));
		} finally {
			setWaitingAddNote(false);
		}
	};

	return (
		<>
			<div className={`${styles.notesContainer} ${className ?? ""}`}>
				{sessionNotes.length === 0 ? (
					<NoResults text={i18n.t("result.components.resultsPage.proctoringResultsViewTSX.noNotesAdded")} add={true} />
				) : (
					<div className={styles.sideBarContentNotes}>
						{sessionNotes.map((v) => (
							<SessionNoteRow
								key={v.id}
								sessionNote={v}
								thumbnails={thumbnails}
								offsetGetter={timeOffset}
								dispatch={dispatch}
								editNote={editNote}
								deleteNote={deleteNote}
								duration={duration ?? 0}
							/>
						))}
					</div>
				)}
			</div>
			<div className={`${styles.sideBarNotesInput} ${notesInputClassName ?? ""}`}>
				<CoreTextBox
					placeholder={i18n.t("result.components.resultsPage.proctoringResultsViewTSX.enterNotes")}
					height={"45px"}
					onKeyDown={(e) => e.stopPropagation()}
					onChange={(e) => setSideBarNotesNewInput(e.target.value)}
					value={sideBarNotesNewInput}
					disabled={waitingAddNote}
					endAdornment={
						<button
							className={styles.sideBarNotesTextareaButton}
							style={{ cursor: waitingAddNote ? "default" : "pointer" }}
							aria-label={i18n.t("result.components.resultsPage.proctoringResultsViewTSX.saveNotes")}
							onClick={handleAddNote}
							disabled={waitingAddNote}
						>
							{waitingAddNote ? (
								<CoreSpinner size="24px" />
							) : (
								<PaperPlaneIcon style={{ width: "28px", height: "28px" }} />
							)}
						</button>
					}
					style={{
						fontSize: "14px",
						width: "100%",
						height: "47px",
						borderRadius: "8px",
					}}
				/>
			</div>
		</>
	);
}
